import type { DistributionParams, Distributions } from "@custom-types/graphs"
import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import {
  MAX_POSSIBLE_VALUES,
  UNBOUNDED_SUPPORT_FLOOR_OFFSET,
} from "@features/builder/helpers/consts"
import { DfCpdStrictRow } from "@utils/node/dfCpd"
import { isNumericString } from "@utils/string"
export class Validate {
  //--------------------
  // Helpers
  //--------------------
  static #isEmpty = (value: string | null | undefined) =>
    value === undefined || value === null || value.trim() === ""

  /**
   * Where the support of a distribution with unbounded support starts: `loc + offset`.
   * The offset is `1` for Geometric and Logseries, `0` for Poisson.
   *
   * Returns `null` when the row is not one of those distributions, or when `loc` is not a
   * number — other validators cover missing and malformed values.
   */
  static #firstState = (row: DfCpdStrictRow): number | null => {
    const offset = row.distribution
      ? UNBOUNDED_SUPPORT_FLOOR_OFFSET[row.distribution as Distributions]
      : undefined
    if (offset === undefined) return null
    if (!isNumericString(row.loc)) return null

    return Number(row.loc) + offset
  }

  /** Lowest value the node can actually take: `max(loc + offset, lbound)`. */
  static #lowestState = (row: DfCpdStrictRow): number | null => {
    const firstState = Validate.#firstState(row)
    if (firstState === null) return null
    if (!isNumericString(row.lbound)) return firstState

    return Math.max(firstState, Number(row.lbound))
  }

  //--------------------
  // Validations
  //--------------------
  static scale(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.scale)) {
      return errors
    }

    if (!isNumericString(row.scale)) {
      errors.push({
        msg: "scale need to be an number",
        meta: { row: idx, col: ["scale"] },
      })
    }

    const scale = Number(row.scale)
    if (scale < 0)
      errors.push({ msg: "scale cant be negative number", meta: { row: idx, col: ["scale"] } })

    return errors
  }

  //  @TODO (Bartek Laskowski) 2026-08-25: do we need to validate loc at all? Maybe log warning in devtool, but how to distinguish {nodeName} from invalid number string
  static loc(_idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (!isNumericString(row.loc)) {
      return errors
    }

    return errors
  }

  static a(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.a)) {
      return errors
    }

    if (!isNumericString(row.a)) {
      errors.push({ msg: "a need to be an number", meta: { row: idx, col: ["a"] } })
      return errors
    }

    if (Number(row.a) <= 0) {
      errors.push({ msg: "a need to be greater than 0", meta: { row: idx, col: ["a"] } })
    }

    return errors
  }

  static b(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.b)) {
      return errors
    }

    if (!isNumericString(row.b)) {
      errors.push({ msg: "b need to be an number", meta: { row: idx, col: ["b"] } })
      return errors
    }

    if (Number(row.b) <= 0) {
      errors.push({ msg: "b need to be greater than 0", meta: { row: idx, col: ["b"] } })
    }

    return errors
  }

  static c(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.c)) {
      return errors
    }

    if (!isNumericString(row.c)) {
      errors.push({ msg: "c need to be an number", meta: { row: idx, col: ["c"] } })
      return errors
    }

    const shapeParameter = Number(row.c)
    if (shapeParameter <= 0 || shapeParameter >= 1) {
      errors.push({
        msg: "c need to be greater than 0 and less than 1",
        meta: { row: idx, col: ["c"] },
      })
    }

    return errors
  }

  static cPositive(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.c)) {
      return errors
    }

    if (!isNumericString(row.c)) {
      errors.push({ msg: "c need to be an number", meta: { row: idx, col: ["c"] } })
      return errors
    }

    if (Number(row.c) <= 0) {
      errors.push({ msg: "c need to be greater than 0", meta: { row: idx, col: ["c"] } })
    }

    return errors
  }

  static n(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.n)) {
      return errors
    }

    if (!isNumericString(row.n)) {
      errors.push({ msg: "n need to be an number", meta: { row: idx, col: ["n"] } })
      return errors
    }

    const numTrials = Number(row.n)
    if (numTrials < 0) {
      errors.push({ msg: "n need to be equal or greater than 0", meta: { row: idx, col: ["n"] } })
    }

    if (!Number.isInteger(numTrials)) {
      errors.push({ msg: "n need to be a whole number", meta: { row: idx, col: ["n"] } })
    }

    return errors
  }

  static nHypergeometric(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.n) || Validate.#isEmpty(row.M)) {
      return errors
    }

    if (!isNumericString(row.n)) {
      errors.push({ msg: "n need to be an number", meta: { row: idx, col: ["n"] } })
      return errors
    }

    const n = Number(row.n)
    if (n < 0) {
      errors.push({ msg: "n need to be greater or equal 0", meta: { row: idx, col: ["n"] } })
    }

    if (!Number.isInteger(n)) {
      errors.push({ msg: "n need to be a whole number", meta: { row: idx, col: ["n"] } })
    }

    if (isNumericString(row.M)) {
      const M = Number(row.M)
      if (n >= M) {
        errors.push({ msg: "n need to be less than M", meta: { row: idx, col: ["n", "M"] } })
      }
    }

    return errors
  }

  static p(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.p)) {
      return errors
    }

    if (!isNumericString(row.p)) {
      errors.push({ msg: "p need to be an number", meta: { row: idx, col: ["p"] } })
      return errors
    }

    const probability = Number(row.p)
    if (probability <= 0 || probability >= 1) {
      errors.push({
        msg: "p need to be greater than 0 and less than 1",
        meta: { row: idx, col: ["p"] },
      })
    }

    return errors
  }

  static pAllowOne(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.p)) {
      return errors
    }

    if (!isNumericString(row.p)) {
      errors.push({ msg: "p need to be an number", meta: { row: idx, col: ["p"] } })
      return errors
    }

    const probability = Number(row.p)
    if (probability <= 0 || probability > 1) {
      errors.push({
        msg: "p need to be greater than 0 and less or equal 1",
        meta: { row: idx, col: ["p"] },
      })
    }

    return errors
  }

  static df(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.df)) {
      return errors
    }

    if (!isNumericString(row.df)) {
      errors.push({ msg: "df need to be an number", meta: { row: idx, col: ["df"] } })
      return errors
    }

    const degreesOfFreedom = Number(row.df)
    if (degreesOfFreedom <= 0) {
      errors.push({ msg: "df need to be greater than 0", meta: { row: idx, col: ["df"] } })
    }

    if (!Number.isInteger(degreesOfFreedom)) {
      errors.push({ msg: "df need to be a whole number", meta: { row: idx, col: ["df"] } })
    }

    return errors
  }

  static dfn(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.dfn)) {
      return errors
    }

    if (!isNumericString(row.dfn)) {
      errors.push({ msg: "dfn need to be an number", meta: { row: idx, col: ["dfn"] } })
      return errors
    }

    if (Number(row.dfn) <= 0) {
      errors.push({ msg: "dfn need to be greater than 0", meta: { row: idx, col: ["dfn"] } })
    }

    return errors
  }

  static dfd(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.dfd)) {
      return errors
    }

    if (!isNumericString(row.dfd)) {
      errors.push({ msg: "dfd need to be an number", meta: { row: idx, col: ["dfd"] } })
      return errors
    }

    if (Number(row.dfd) <= 0) {
      errors.push({ msg: "dfd need to be greater than 0", meta: { row: idx, col: ["dfd"] } })
    }

    return errors
  }

  static M(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.M)) {
      return errors
    }

    if (!isNumericString(row.M)) {
      errors.push({ msg: "M need to be an number", meta: { row: idx, col: ["M"] } })
      return errors
    }

    const M = Number(row.M)
    if (M <= 0) {
      errors.push({ msg: "M need to be greater than 0", meta: { row: idx, col: ["M"] } })
    }

    if (!Number.isInteger(M)) {
      errors.push({ msg: "M need to be a whole number", meta: { row: idx, col: ["M"] } })
    }

    return errors
  }

  static N(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.N)) {
      return errors
    }

    if (!isNumericString(row.N)) {
      errors.push({ msg: "N need to be an number", meta: { row: idx, col: ["N"] } })
      return errors
    }

    const N = Number(row.N)
    if (N <= 0) {
      errors.push({ msg: "N need to be greater than 0", meta: { row: idx, col: ["N"] } })
    }

    if (!Number.isInteger(N)) {
      errors.push({ msg: "N need to be a whole number", meta: { row: idx, col: ["N"] } })
    }

    if (isNumericString(row.M)) {
      const M = Number(row.M)
      if (N >= M) {
        errors.push({ msg: "N need to be less than M", meta: { row: idx, col: ["N", "M"] } })
      }
    }

    return errors
  }

  static mu(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.mu)) {
      return errors
    }

    if (!isNumericString(row.mu)) {
      errors.push({ msg: "mu need to be an number", meta: { row: idx, col: ["mu"] } })
      return errors
    }

    if (Number(row.mu) < 0) {
      errors.push({ msg: "mu need to be greater or equal 0", meta: { row: idx, col: ["mu"] } })
    }

    return errors
  }

  static s(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.s)) {
      return errors
    }

    if (!isNumericString(row.s)) {
      errors.push({ msg: "s need to be an number", meta: { row: idx, col: ["s"] } })
      return errors
    }

    if (Number(row.s) <= 0) {
      errors.push({ msg: "s need to be greater than 0", meta: { row: idx, col: ["s"] } })
    }

    return errors
  }

  static nc(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.nc)) {
      return errors
    }

    if (!isNumericString(row.nc)) {
      errors.push({ msg: "nc need to be an number", meta: { row: idx, col: ["nc"] } })
      return errors
    }

    if (Number(row.nc) <= 0) {
      errors.push({ msg: "nc need to be greater than 0", meta: { row: idx, col: ["nc"] } })
    }

    return errors
  }

  static kappa(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (Validate.#isEmpty(row.kappa)) {
      return errors
    }

    if (!isNumericString(row.kappa)) {
      errors.push({ msg: "kappa need to be an number", meta: { row: idx, col: ["kappa"] } })
      return errors
    }

    if (Number(row.kappa) <= 0) {
      errors.push({ msg: "kappa need to be greater than 0", meta: { row: idx, col: ["kappa"] } })
    }

    return errors
  }

  static bounds(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    if (!isNumericString(row.lbound) && !isNumericString(row.ubound)) {
      return errors
    }

    const lbound = row.lbound ? Number(row.lbound) : null
    const ubound = row.ubound ? Number(row.ubound) : null

    if (lbound !== null && ubound !== null && lbound > ubound)
      errors.push({
        msg: "lbound need to be less or equal ubound",
        meta: { row: idx, col: ["lbound", "ubound"] },
      })

    return errors
  }

  /**
   * Distributions with unbounded support only — Geometric, Logseries and Poisson.
   *
   * Their states are `[max(loc + offset, lbound), ubound]` with `ubound` inclusive, where
   * the offset is `1` for Geometric and Logseries and `0` for Poisson. When that range is
   * empty the sampler fails with an opaque
   * "Error while initializing '<distribution>' distribution", and a parent with no values
   * used to wipe its children's df_cpd.
   *
   * Do not call this for other distributions — the continuous ones legitimately allow
   * `loc` outside `[lbound, ubound]`.
   */
  static nonEmptyStateSpace(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    const lowestState = Validate.#lowestState(row)
    if (lowestState === null || !isNumericString(row.ubound)) {
      return errors
    }

    if (lowestState > Number(row.ubound)) {
      errors.push({
        msg: `ubound need to be greater or equal ${lowestState}, otherwise the node has no possible values`,
        meta: { row: idx, col: ["ubound"] },
      })
    }

    return errors
  }

  /**
   * Distributions with unbounded support only — Geometric, Logseries and Poisson.
   *
   * Their support is infinite, so nothing stops `ubound` from being set arbitrarily high
   * and multiplying a child's row count. Allow at most `MAX_POSSIBLE_VALUES` of them,
   * counted from the support floor so the cap is the same for all three.
   */
  static uboundWithinLimit(idx: number, row: DfCpdStrictRow): ValidationState["errors"] {
    const errors: ValidationState["errors"] = []

    const firstState = Validate.#firstState(row)
    if (firstState === null || !isNumericString(row.ubound)) {
      return errors
    }

    const maxUbound = firstState + MAX_POSSIBLE_VALUES - 1

    if (Number(row.ubound) > maxUbound) {
      errors.push({
        msg: `ubound need to be less or equal ${maxUbound}`,
        meta: { row: idx, col: ["ubound"] },
      })
    }

    return errors
  }

  /**
   * Distributions with unbounded support only — Geometric, Logseries and Poisson.
   *
   * All have infinite support, so `ubound` is the only thing that makes their possible
   * values enumerable. A leaf is merely sampled and needs no upper cut, but a node with
   * children has its values enumerated to build each child's df_cpd rows — and an
   * infinite support cannot be enumerated.
   */
  static uboundRequiredWithChildren(
    idx: number,
    row: DfCpdStrictRow,
    hasChildren: boolean,
  ): ValidationState["errors"] {
    if (!hasChildren) return []
    if (!Validate.#isEmpty(row.ubound)) return []

    return [
      { msg: "ubound is required once this node has a child", meta: { row: idx, col: ["ubound"] } },
    ]
  }

  static mandatoryParams(
    idx: number,
    row: DfCpdStrictRow,
    params: DistributionParams[],
  ): ValidationState["errors"] {
    const missingParams: DistributionParams[] = []

    const rowParams = Object.keys(row)

    for (const param of params) {
      if (!rowParams.includes(param) || Validate.#isEmpty(row[param])) {
        missingParams.push(param)
      }
    }

    if (missingParams.length) {
      return [
        {
          msg: `${row.distribution} distribution require param(s) [${missingParams.join(", ")}]`,
          meta: { row: idx, col: missingParams },
        },
      ]
    } else {
      return []
    }
  }
}
